import type { WaypointInput } from '../types/jobs';
import jobService from './jobs';
import sprayService from './spray';

const DB_NAME = 'factree-waypoints';
const DB_VERSION = 1;
const STORE_NAME = 'pending';

interface QueueRecord {
  id?: number;
  entityType: 'job' | 'spray';
  entityId: number;
  waypoint: WaypointInput;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// IndexedDB helpers
// ---------------------------------------------------------------------------

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('entityKey', ['entityType', 'entityId'], { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx(db: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
  return db.transaction(STORE_NAME, mode).objectStore(STORE_NAME);
}

// ---------------------------------------------------------------------------
// WaypointQueue — IndexedDB-backed offline queue
// ---------------------------------------------------------------------------

// Adaptive auto-sync pacing. Each upload wakes the phone's mobile radio,
// which then stays at full power for several seconds after the send, so it
// is the number of uploads that costs battery, not their size. Points are
// therefore sent on a timer set by the work being tracked (`uploadEveryMs` in
// utils/gpsCadence: 2 min for a spray or a coverage job, 5 min for a trail),
// not every few points. A mower keeping a point every 3 s used to send every
// 30 s, with the radio barely ever idle.
//
// Points go up sooner when they are needed: the app coming back on screen,
// signal returning, a tank finished (`sendNow`), and a finish (`uploadAll`).
const DEFAULT_SYNC_MS = 60_000; // nothing tracking: what is queued, from before
const MAX_SYNC_MS = 300_000;    // cap when idle / failing (5 min)
const BACKOFF_FACTOR = 1.6;
// A safety valve, not the pacing: send at once if this many points pile up
// between timer ticks. Half a batch, so it still goes in one request.
const FLUSH_AT_BUFFER_SIZE = 250;
// Points per upload. The queue has no storage cap — a phone out of signal for
// a whole spray keeps every fix — so this only sets how fast a backlog clears
// once signal returns. At 50 a 4-hour offline spray (≈1,440 fixes at 10 s)
// took the best part of 20 minutes to catch up, and anything still queued at
// sign-off was refused as SPRAY_CLOSED and discarded. 500 is ~60 KB of JSON
// and the spray route's own `.max(500)`.
export const UPLOAD_BATCH_SIZE = 500;

type BatchOutcome = 'empty' | 'ok' | 'failed' | 'closed' | 'unauthorized';

export interface UploadAllResult {
  /** Points uploaded by this call. */
  uploaded: number;
  /** Points for the entity still on the phone afterwards. */
  remaining: number;
  /** Why it stopped short, when it did. */
  error: QueueStatus['lastError'];
}

// Server codes meaning "these points will never be accepted": the job or spray
// is finished, cancelled, deleted, or no longer assigned to this operator.
// Holding them is not caution, it is a leak — the entity stays in listEntities
// forever, so every flush posts a batch that is certain to be refused. Seven
// closed jobs were doing that once a minute on one device, permanently.
//
// Only terminal codes belong here. A paused entity keeps its points: the whole
// reason the queue survives a pause is that the operator is coming back.
export const TERMINAL_UPLOAD_CODES = new Set<string>([
  'ASSIGNMENT_CLOSED',
  'SPRAY_CLOSED',
  'SPRAY_NOT_FOUND',
]);

// Backstop for a terminal state nobody anticipated. The codes above are the
// real fix; this is what stops the next gap in them becoming another immortal
// queue. Seven days is far past any plausible offline stint — a queue belongs
// to one job, and a job is a day — so nothing a working operator recorded is
// ever in range.
const MAX_RECORD_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface QueueStatus {
  /** Last error observed when trying to upload a batch (null once we succeed). */
  lastError: {
    status?: number;
    code?: string;
    message: string;
    at: number;
    /** Which entity the failed batch was for — lets the tracking controller
     *  decide whether to react (e.g. only stop capture if the failing entity
     *  is the one we're currently capturing for). */
    entityType?: 'job' | 'spray';
    entityId?: number;
  } | null;
  /** Timestamp of the last successful upload (ms epoch). */
  lastSuccessAt: number | null;
  /** Consecutive failed batch attempts since the last success. */
  consecutiveFailures: number;
}

type StatusListener = (s: QueueStatus) => void;

class WaypointQueue {
  private db: IDBDatabase | null = null;
  private flushing = false;
  private syncTimer: ReturnType<typeof setTimeout> | null = null;
  private baseSyncMs = DEFAULT_SYNC_MS;
  private syncIntervalMs = DEFAULT_SYNC_MS;
  private listenersAttached = false;
  // Approximate in-flight count since the last successful flush. Used to
  // trigger an immediate flush when the buffer crosses FLUSH_AT_BUFFER_SIZE
  // without waiting for the timer.
  private bufferedSinceFlush = 0;
  // When true, auto-sync is paused because the server returned 401 — we don't
  // know how to recover from inside the queue, so we wait for the auth layer
  // (or an explicit retry) to clear the condition before resuming.
  private pausedFor401 = false;

  // Diagnostics surface (observable by the UI so the operator can see why
  // points are piling up).
  private status: QueueStatus = {
    lastError: null,
    lastSuccessAt: null,
    consecutiveFailures: 0,
  };
  private statusListeners = new Set<StatusListener>();

  getStatus(): QueueStatus {
    return this.status;
  }

  subscribeStatus(cb: StatusListener): () => void {
    this.statusListeners.add(cb);
    return () => { this.statusListeners.delete(cb); };
  }

  private setStatus(partial: Partial<QueueStatus>): void {
    this.status = { ...this.status, ...partial };
    for (const l of this.statusListeners) {
      try { l(this.status); } catch (e) { console.error('[WaypointQueue] status listener error', e); }
    }
  }

  /** Ensure DB is open. */
  private async getDb(): Promise<IDBDatabase> {
    if (!this.db) this.db = await openDb();
    return this.db;
  }

  /**
   * How often to send while something is being tracked; null puts it back to
   * the default. Takes effect from the next tick.
   */
  setUploadEvery(ms: number | null): void {
    const next = ms ?? DEFAULT_SYNC_MS;
    if (next === this.baseSyncMs) return;
    this.baseSyncMs = next;
    this.syncIntervalMs = next;
    if (this.syncTimer != null && this.listenersAttached) {
      clearTimeout(this.syncTimer);
      this.scheduleNextSync();
    }
  }

  /**
   * Send one entity's queued points now, for a moment when the server is
   * about to judge them (a tank being finished). Waits at most `maxMs`: out
   * of signal the caller carries on and the points go later, as they always
   * have. Resolves true when nothing for the entity is left on the phone.
   */
  async sendNow(entityType: 'job' | 'spray', entityId: number, maxMs = 5_000): Promise<boolean> {
    if (this.pausedFor401) return false;
    if ((await this.pendingCount(entityType, entityId)) === 0) return true;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
    const done = this.uploadAll(entityType, entityId).then((r) => r.remaining === 0).catch(() => false);
    const late = new Promise<boolean>((r) => setTimeout(() => r(false), maxMs));
    return Promise.race([done, late]);
  }

  /** Add a single waypoint to the persistent queue. */
  async enqueue(entityType: 'job' | 'spray', entityId: number, wp: WaypointInput): Promise<void> {
    const db = await this.getDb();
    const record: QueueRecord = { entityType, entityId, waypoint: wp, createdAt: new Date().toISOString() };
    await new Promise<void>((resolve, reject) => {
      const req = tx(db, 'readwrite').add(record);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
    this.bufferedSinceFlush += 1;
    // Only a pile-up sends early; otherwise the timer sets the pace (don't
    // keep resetting it).
    if (this.bufferedSinceFlush >= FLUSH_AT_BUFFER_SIZE && !this.pausedFor401) {
      void this.flush();
    }
  }

  /**
   * Force a quick sync by collapsing the interval to the base pace and
   * rescheduling the timer. Called when connectivity returns or the tab
   * becomes visible — NOT on every enqueue (that would keep clearing the
   * timer and starve uploads when GPS fixes arrive faster than the
   * baseline cadence).
   */
  private bumpSyncNow(): void {
    if (this.pausedFor401) return;
    this.syncIntervalMs = this.baseSyncMs;
    if (this.syncTimer != null) {
      clearTimeout(this.syncTimer);
      this.scheduleNextSync();
    }
  }

  private scheduleNextSync(): void {
    this.syncTimer = setTimeout(async () => {
      try {
        const synced = await this.flush();
        if (synced > 0) {
          // Productive sync — stay aggressive.
          this.syncIntervalMs = this.baseSyncMs;
        } else {
          // Nothing to send (or send failed) — back off up to the cap.
          this.syncIntervalMs = Math.min(
            Math.round(this.syncIntervalMs * BACKOFF_FACTOR),
            MAX_SYNC_MS,
          );
        }
      } catch {
        this.syncIntervalMs = Math.min(
          Math.round(this.syncIntervalMs * BACKOFF_FACTOR),
          MAX_SYNC_MS,
        );
      } finally {
        // Only reschedule if auto-sync is still armed.
        if (this.listenersAttached) this.scheduleNextSync();
      }
    }, this.syncIntervalMs);
  }

  /**
   * Flush queued waypoints to the server.
   *
   * Walks each *distinct entity* in the queue and tries to upload one batch
   * (UPLOAD_BATCH_SIZE) of its points per pass. Critically: a 409 (or any other failure) on one
   * entity does NOT block other entities from being attempted in the same
   * flush. Without this, stuck records from a paused job would dominate the
   * `createdAt`-ordered head of the queue and starve uploads for every other
   * entity — observed in the field after a job was left paused server-side
   * with 131 points queued, blocking a fresh job's waypoints from ever
   * reaching the server.
   *
   * With `drain: true`, loops until either every entity is empty or 10 s
   * elapse. Default (single pass per entity) is used by the periodic
   * auto-sync.
   */
  async flush(opts: { drain?: boolean } = {}): Promise<number> {
    if (this.flushing) return 0;
    if (this.pausedFor401 && !opts.drain) return 0;
    this.flushing = true;

    const deadline = opts.drain ? Date.now() + 10_000 : 0;
    let totalSynced = 0;

    try {
      const db = await this.getDb();
      await this.purgeExpired(db);

      do {
        const entities = await this.listEntities(db);
        if (entities.length === 0) break;

        let passSynced = 0;
        // Track per-entity skip for *this drain loop* — once an entity fails
        // we don't keep retrying it inside the same flush call.
        const skip = new Set<string>();

        for (const ent of entities) {
          const key = `${ent.entityType}:${ent.entityId}`;
          if (skip.has(key)) continue;
          if (opts.drain && Date.now() >= deadline) break;

          const { outcome, count } = await this.uploadBatch(db, ent.entityType, ent.entityId);
          if (outcome === 'ok') {
            passSynced += count;
          } else if (outcome === 'unauthorized') {
            break;
          } else if (outcome !== 'empty') {
            skip.add(key);
          }
        }

        totalSynced += passSynced;

        // In drain mode: stop if we made no progress and at least one entity
        // failed (otherwise we'd loop forever on a stuck entity), or if every
        // remaining entity is in the skip set.
        if (opts.drain && passSynced === 0) break;
      } while (opts.drain && Date.now() < deadline);

      // A productive flush should drag the auto-sync timer back to the fast
      // cadence — otherwise after a long backoff the next scheduled tick is
      // still up to MAX_SYNC_MS away, even though we just proved the server
      // is reachable. Skipped if auto-sync isn't running (e.g. paused on 401).
      if (totalSynced > 0 && this.listenersAttached) {
        this.syncIntervalMs = this.baseSyncMs;
        if (this.syncTimer != null) {
          clearTimeout(this.syncTimer);
          this.scheduleNextSync();
        }
      }

      return totalSynced;
    } finally {
      this.flushing = false;
    }
  }

  /**
   * Upload every queued point for one entity, batch after batch, until it is
   * empty or a batch fails. For finishing a job or spray: once it is closed
   * the server refuses its points for good and the queue discards them, so
   * whatever is still on the phone at that moment is lost. Unlike
   * `flush({ drain: true })` there is no time limit — a long offline stint
   * is exactly the case that needs more than 10 s.
   *
   * Waits for an in-flight flush rather than skipping, so a batch is never
   * posted twice. Tries even while paused on a 401, like a drain.
   */
  async uploadAll(
    entityType: 'job' | 'spray',
    entityId: number,
    onProgress?: (remaining: number) => void,
  ): Promise<UploadAllResult> {
    while (this.flushing) await new Promise((r) => setTimeout(r, 100));
    this.flushing = true;

    let uploaded = 0;
    let error: QueueStatus['lastError'] = null;
    try {
      const db = await this.getDb();
      onProgress?.(await this.pendingCount(entityType, entityId));
      for (;;) {
        const { outcome, count } = await this.uploadBatch(db, entityType, entityId);
        if (outcome === 'empty') break;
        if (outcome !== 'ok') {
          error = this.status.lastError;
          break;
        }
        uploaded += count;
        onProgress?.(await this.pendingCount(entityType, entityId));
      }
      return { uploaded, remaining: await this.pendingCount(entityType, entityId), error };
    } finally {
      this.flushing = false;
    }
  }

  /**
   * Post the oldest UPLOAD_BATCH_SIZE points for one entity and record the
   * outcome in the queue status. Shared by `flush` and `uploadAll`.
   */
  private async uploadBatch(
    db: IDBDatabase,
    entityType: 'job' | 'spray',
    entityId: number,
  ): Promise<{ outcome: BatchOutcome; count: number }> {
    const records = await this.readByEntityLimited(db, entityType, entityId, UPLOAD_BATCH_SIZE);
    if (records.length === 0) return { outcome: 'empty', count: 0 };

    const waypoints = records.map((r) => r.waypoint);
    try {
      const ok = await this.postWaypoints(entityType, entityId, waypoints);
      if (ok) {
        await this.deleteRecords(db, records.map((r) => r.id!));
        this.bufferedSinceFlush = 0;
        this.setStatus({
          lastError: null,
          lastSuccessAt: Date.now(),
          consecutiveFailures: 0,
        });
        return { outcome: 'ok', count: records.length };
      }
      // Server responded without success — e.g. {success:false} body.
      this.setStatus({
        lastError: {
          message: 'Server rejected upload (success:false)',
          at: Date.now(),
          entityType,
          entityId,
        },
        consecutiveFailures: this.status.consecutiveFailures + 1,
      });
      console.warn(
        `[WaypointQueue] upload for ${entityType}:${entityId} ` +
        `returned success:false — ${records.length} points remain queued`,
      );
      return { outcome: 'failed', count: 0 };
    } catch (e) {
      // apiService rejects with an ApiError: `status` and `code` sit on the
      // error itself, and a request that got no answer at all is `offline`
      // with a status of 500. Reading `code` only from `response.data` (the
      // raw axios shape) meant no terminal refusal was ever recognised, and a
      // dead connection read as a server fault.
      const err = e as {
        status?: number;
        code?: string;
        offline?: boolean;
        response?: { status?: number; data?: { error?: { message?: string; code?: string } } };
        message?: string;
      };
      const status = err.offline ? undefined : (err.status ?? err.response?.status);
      const code = err.code ?? err.response?.data?.error?.code;
      const serverMsg = err.response?.data?.error?.message;
      const message = serverMsg || err.message || 'Unknown network error';

      this.setStatus({
        lastError: { status, code, message, at: Date.now(), entityType, entityId },
        consecutiveFailures: this.status.consecutiveFailures + 1,
      });

      // Terminal refusal: the server will never take these. Drop every
      // record for the entity, not just this batch, or the remainder simply
      // becomes the next batch to be refused forever.
      if (code && TERMINAL_UPLOAD_CODES.has(code)) {
        await this.clear(entityType, entityId);
        console.warn(
          `[WaypointQueue] ${entityType}:${entityId} is closed ` +
          `(${code}) — discarded ${records.length}+ queued points that ` +
          `can never be accepted`,
        );
        return { outcome: 'closed', count: 0 };
      }

      // 401 means auth has expired. The Axios refresh-on-401 interceptor
      // already handled (and failed) recovery — no point retrying. Stop
      // auto-sync; queued records stay in IndexedDB so they can upload
      // after the user re-authenticates. A separate task ("Stop waypoint
      // tracker on 401") is responsible for tearing down GPS capture.
      if (status === 401) {
        this.pausedFor401 = true;
        this.stopAutoSync();
        console.warn('[WaypointQueue] paused on 401 — auth required before more uploads');
        return { outcome: 'unauthorized', count: 0 };
      }
      console.warn(
        `[WaypointQueue] flush failed for ${entityType}:${entityId} ` +
        `status=${status ?? 'n/a'} code=${code ?? 'n/a'} msg="${message}" pending=${records.length}`,
        e,
      );
      // Records stay queued for this entity — a 409 means the entity is
      // paused; the next manual retry / resume will pick them up.
      return { outcome: 'failed', count: 0 };
    }
  }

  /**
   * Drop records older than MAX_RECORD_AGE_MS.
   *
   * Walks the `createdAt` index from the oldest end and stops at the first
   * record inside the window, so the normal case — nothing expired — costs
   * one cursor step rather than a full scan on every flush.
   */
  private purgeExpired(db: IDBDatabase): Promise<void> {
    const cutoff = new Date(Date.now() - MAX_RECORD_AGE_MS).toISOString();
    return new Promise((resolve, reject) => {
      const ids: number[] = [];
      const idx = tx(db, 'readonly').index('createdAt');
      const req = idx.openCursor(IDBKeyRange.upperBound(cutoff, true));
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          ids.push((cursor.value as QueueRecord).id!);
          cursor.continue();
          return;
        }
        if (ids.length === 0) { resolve(); return; }
        console.warn(
          `[WaypointQueue] discarded ${ids.length} queued point(s) older than ` +
          `${Math.round(MAX_RECORD_AGE_MS / 86_400_000)} days`,
        );
        this.deleteRecords(db, ids).then(resolve, reject);
      };
      req.onerror = () => reject(req.error);
    });
  }

  /** Get count of pending waypoints, optionally filtered. */
  async pendingCount(entityType?: string, entityId?: number): Promise<number> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      if (entityType != null && entityId != null) {
        const idx = tx(db, 'readonly').index('entityKey');
        const req = idx.count(IDBKeyRange.only([entityType, entityId]));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      } else {
        const req = tx(db, 'readonly').count();
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }
    });
  }

  /** Clear all queued records for a given entity. */
  async clear(entityType: string, entityId: number): Promise<void> {
    const db = await this.getDb();
    const records = await this.readByEntity(db, entityType, entityId);
    if (records.length > 0) {
      await this.deleteRecords(db, records.map((r) => r.id!));
    }
  }

  /**
   * Start automatic sync listeners.
   * Uses an adaptive timer: the base pace when data is flowing, backing off
   * (×1.6, capped at MAX_SYNC_MS) when nothing syncs. Back to the base pace
   * after any upload, tab focus, or network reconnect.
   */
  startAutoSync(): void {
    if (this.listenersAttached) return;
    this.listenersAttached = true;
    // A new tracking session implies auth is current again — clear the 401
    // pause so we'll attempt uploads on the next tick.
    this.pausedFor401 = false;

    document.addEventListener('visibilitychange', this.handleVisibility);
    window.addEventListener('online', this.handleOnline);

    this.syncIntervalMs = this.baseSyncMs;
    this.scheduleNextSync();
  }

  /**
   * Somebody is signed in: lift a 401 pause and send whatever is queued.
   * Called by `TrackingWatcher` on every authenticated mount, so points kept
   * through a sign-out — or left from any earlier session — go up without
   * waiting for tracking to start again. A queue with nothing in it starts
   * nothing.
   */
  async resumeAfterSignIn(): Promise<void> {
    this.pausedFor401 = false;
    if (this.listenersAttached) {
      this.bumpSyncNow();
      void this.flush();
      return;
    }
    if ((await this.pendingCount()) === 0) return;
    this.startAutoSync();
    void this.flush();
  }

  /** Stop automatic sync listeners. */
  stopAutoSync(): void {
    this.listenersAttached = false;
    document.removeEventListener('visibilitychange', this.handleVisibility);
    window.removeEventListener('online', this.handleOnline);
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  private handleVisibility = (): void => {
    if (document.visibilityState === 'visible') {
      // Someone is looking: send now, and get the timer back to its pace.
      this.bumpSyncNow();
      this.flush();
    } else if (!this.pausedFor401) {
      // Leaving the app: the radio is likely awake from whatever they just
      // did, so this send is nearly free, and the track is current for
      // anyone watching while the phone sits in a pocket.
      void this.flush();
    }
  };

  private handleOnline = (): void => {
    this.bumpSyncNow();
    this.flush();
  };

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  /**
   * Return the distinct entities currently represented in the queue, ordered
   * by their *most recent* record first — so a freshly-tracked entity is
   * tried before older stuck ones.
   */
  private listEntities(db: IDBDatabase): Promise<{ entityType: 'job' | 'spray'; entityId: number; latestAt: string }[]> {
    return new Promise((resolve, reject) => {
      const map = new Map<string, { entityType: 'job' | 'spray'; entityId: number; latestAt: string }>();
      const req = tx(db, 'readonly').openCursor();
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) {
          const arr = [...map.values()].sort((a, b) => (a.latestAt < b.latestAt ? 1 : -1));
          resolve(arr);
          return;
        }
        const v = cursor.value as QueueRecord;
        const key = `${v.entityType}:${v.entityId}`;
        const existing = map.get(key);
        if (!existing || v.createdAt > existing.latestAt) {
          map.set(key, { entityType: v.entityType, entityId: v.entityId, latestAt: v.createdAt });
        }
        cursor.continue();
      };
      req.onerror = () => reject(req.error);
    });
  }

  private readByEntityLimited(
    db: IDBDatabase,
    entityType: string,
    entityId: number,
    limit: number,
  ): Promise<QueueRecord[]> {
    return new Promise((resolve, reject) => {
      const results: QueueRecord[] = [];
      const idx = tx(db, 'readonly').index('entityKey');
      const req = idx.openCursor(IDBKeyRange.only([entityType, entityId]));
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor && results.length < limit) {
          results.push(cursor.value);
          cursor.continue();
        } else {
          resolve(results);
        }
      };
      req.onerror = () => reject(req.error);
    });
  }

  private readByEntity(db: IDBDatabase, entityType: string, entityId: number): Promise<QueueRecord[]> {
    return new Promise((resolve, reject) => {
      const results: QueueRecord[] = [];
      const idx = tx(db, 'readonly').index('entityKey');
      const req = idx.openCursor(IDBKeyRange.only([entityType, entityId]));
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          results.push(cursor.value);
          cursor.continue();
        } else {
          resolve(results);
        }
      };
      req.onerror = () => reject(req.error);
    });
  }

  private deleteRecords(db: IDBDatabase, ids: number[]): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = tx(db, 'readwrite');
      let remaining = ids.length;
      if (remaining === 0) { resolve(); return; }
      for (const id of ids) {
        const req = store.delete(id);
        req.onsuccess = () => { if (--remaining === 0) resolve(); };
        req.onerror = () => reject(req.error);
      }
    });
  }

  private async postWaypoints(entityType: 'job' | 'spray', entityId: number, waypoints: WaypointInput[]): Promise<boolean> {
    if (entityType === 'job') {
      const res = await jobService.postWaypoints(entityId, waypoints);
      return res.success;
    } else {
      const res = await sprayService.postWaypoints(entityId, waypoints);
      return res.success;
    }
  }
}

const waypointQueue = new WaypointQueue();
export default waypointQueue;
